"use strict";

var Backbone = require('dibs-backbone');
var _ = require('underscore');
var $ = require('dibs-jquery-shim');
var extendCompose = require('bunsen/libraries/extendCompose');
const composeClassNames = require('compose-class-names');

/**
 * @class TemplateView is a lightweight view for rapid prototyping with a concise syntax, use it when you don't care to create a bunch of subclasses just to show a set of mostly static templates. To render a model's toJSON() into a template and package it into a Backbone view instance, it's just:
 *
 * new TemplateView({ // instantiating, not extending here; an instance of the object is ready to use
 *   template: myTemplate, // template can be passed through options or prototype
 *   model: myModel // model.toJSON gets passed to the template as data
 * });
 * You can also nest TemplateView configs inside of a scope property to build more complex views:
 *
 * new TemplateView({
 *   template: outerTemplate,
 *   model: outerModel,
 *   scope: {
 *     'sub-templateview-goes-here': { // plain objects used to create a new TemplateView
 *       template innerTemplate
 *       model: innerModel
 *     },
 *     'not-a-template-view': new MyOtherView() // instances of Backbone.View get appended directly
 *   }
 * });
 * where outerTemplate is:
 *
 * <div>
 *   <div data-append='sub-templateview-goes-here'>
 *     <!-- subview gets appended here, the element is
 *          identified by the key in the TemplateView
 *          instance's scope map -->
 *   </div>
 *   <div data-append='not-a-template-view'>
 *     <!-- myOtherview instance gets appended here -->
 *   </div>
 * </div>
 *
 * some notes:
 *  - the template can be specified on the prototype or through options.
 *  - this.model.toJSON() is automatically offered as as the data for the template
 *  - automatically appends subviews using a scope map, passed through prototype or options. Elements on which to append are declared with a data-append=<name-in-scope> attribute
 *
 * @param {boolean} templateSpec.useInnerElement - if true, then the first element in the template will be used as the element for the view, otherwise the template will be rendered into a wrapper element.
 * @param {boolean} templateSpec.local - a local-css map as exported by css-loader. It will be available as obj.local in your template.
 * @param {object} options.scope: a map of subviews to append to subview parent elements.
 *   the values in the map can of one of:
 *      @property {Function} - value is derived from execution
 *      @property {Backbone.View} - value is used directly
 *      @property {Object} - options object used to construct a new TemplateView
 *      @property {boolean} - if false, the container element will be detached
 *   TemplateView identifies subview parents by elements with a data-append=<subview name> attribute.
 *   TemplateView.Base (the BaseView version of TemplateView) identifies subview parents with a selector of .<subview name>-container.
 *   override getSubElement to change this behavior in your subclass. This is only recommended when subclassing the .NoBaseView version
 * @param {TemplateSpec} options.template : a template specification. Overrides tempalte from prototype if present.
 * @property {TemplateSpec} template :  (required if TemplateSpec is not specified on the prototype) a template specification. Specify on prototype of you inheriting class.
 * @description TemplateSpec identifies your template. It can be:
 *    a string identifying the ID of a template element on the page
 *    a function that works like an underscore template that takes a map of template vars
 *    an object that has the following properties:
 *       src: a function that works like an underscore template
 *       useInnerElement: a boolean that indicates if the first element in the template should be promoted to be this.el via `Backbone.View.prototype.setElement`
 *       local: a local-style map as exported from css-loader. Its values will be exposed to the template as obj.local
 * @property {object} [templateVars]: an object or function that returns the object that will be injected into the template. Defaults to this.model
 * @property .Base - This is a BaseView version of TemplateView that inherits from from Backbone.BaseView instead of Backbone.View. Subveiws are catalogged by BaseView's subview manager, but the user interface is identical.
 */

extendCompose = extendCompose.withMiddleware({afterPrototype: (parentPrototype, childPrototypeBefore, childPrototypeAfter) => {
    if (!childPrototypeAfter.template) {
        return childPrototypeAfter;
    }
    let newLocalClassNames;
    if (childPrototypeAfter.template.local__) {
        newLocalClassNames = composeClassNames(
            childPrototypeAfter.template.local,
            childPrototypeAfter.template.local__
        );
    } else if (childPrototypeAfter.template.__local) {
        newLocalClassNames = composeClassNames(
            childPrototypeAfter.template.__local,
            childPrototypeAfter.template.local
        );
    } else {
        return childPrototypeAfter;
    }
    return Object.assign({},
        childPrototypeAfter,
        {
            template: Object.assign({},
                childPrototypeAfter.template,
                {
                    local: newLocalClassNames
                }
            )
        }
    );
}});
let TemplateView = module.exports = extendCompose.call(Backbone.View, {});
let normalizeToView = require('bunsen/helpers/normalizeToView');
let prototype = {
    templateVars: function () {
        return this.model ? this.model.toJSON() : {};
    },
    getSubElement : function (subViewKey) {
        var subSelector = "[data-append=" + subViewKey + "]";
        var $subs = this.$(subSelector);
        if (this.$el.is(subSelector)) {
            $subs = $subs.add(this.el);
        }
        return $subs.first().get(0);
    },
    getSubView : function (subViewKey) {
        return this._subs[subViewKey];
    },
    initialize: function (options) {
        var scope;
        var useInnerElement;
        var local;
        var $innerEls;
        var renderedTemplate;
        options = options || {};
        scope = options.scope || this.scope || {};
        this.templateVars = options.templateVars || this.templateVars;
        this.template = options.template || this.template;
        if (!_.isFunction(this.template) && this.template instanceof Object) {
            useInnerElement = this.template.useInnerElement;
            local = this.template.local;
            this.template = this.template.src;
        }
        if (undefined === useInnerElement) {
            useInnerElement = useInnerElement || options.useInnerElement;
        }
        if (this.templateVars instanceof Function) {
            this.templateVars = this.templateVars.apply(this);
        }
        if (scope instanceof Function) {
            scope = scope.apply(this);
        }
        if (!this.template) {
            throw new Error("no template provided to template view");
        }
        if (local) {
            this.templateVars = _.extend({local: local}, this.templateVars);
        }
        renderedTemplate = this.template(this.templateVars);
        if (undefined === renderedTemplate) {
            renderedTemplate = "";
        }
        $innerEls = $("<div>").html(renderedTemplate.trim()).contents();
        if (useInnerElement) {
            if ($innerEls.size() > 1) {
                throw new Error("useInnerElement requires template with a single root element.");
            }
            this.setElement($innerEls);
        } else {
            this.$el.append($innerEls);
        }
        this._subs = _.chain(scope).map(function (subViews, appendKey) {
            var el = this.getSubElement(appendKey);
            var $el = $(el);
            var wasArray = true;
            if (!el) {
                return; // couldn't identify a subview parent
            }
            if (!_.isArray(subViews)) {
                subViews = [subViews];
                wasArray = false;
            }
            if (!subViews) {
                return;
            }
            subViews = _.map(subViews, function (subView, i) {
                var innerEl = el;
                var $innerEl;
                if (wasArray) {
                    innerEl = document.createElement(el.tagName);
                    innerEl.setAttribute('data-template-view-child', i);
                    el.appendChild(innerEl);
                }
                $innerEl = $(innerEl);
                subView = normalizeToView({
                    defaultContext: this,
                    defaultParentElement: innerEl,
                    defaultTemplateVars: this.templateVars,
                    content: subView,
                    Constructor: this.defaultConstructor
                });
                if (false === subView) {
                    // TODO: test this
                    $innerEl.detach();
                }
                if (!subView) {
                    return;
                }
                if (innerEl !== subView.el) {
                    $innerEl.append(subView.el);
                }
                return subView;
            }, this);
            if (!wasArray) {
                subViews = _.first(subViews);
            }
            if (this.subViewAppended) {
                this.subViewAppended(subViews);
            }
            if (!subViews) {
                return;
            }
            return [appendKey, subViews];
        }, this).compact().object().value();
        return options;
    },
    render: function () {
        // overriding render so the subviews dont get blown away after initialize runs
        _.each(this._subs, function (subarray) {
            if (!_.isArray(subarray)) {
                subarray = [subarray];
            }
            _.each(subarray, function (sub) {
                _.result(sub, 'render');
            });
        });
        return this;
    }
};
TemplateView.extendSelf(prototype, {
    defaultConstructor: TemplateView
});
let TemplateBaseView = extendCompose.call(Backbone.BaseView, prototype);
TemplateBaseView = module.exports.Base = TemplateBaseView.extend({
    initialize__ : function (options) {
        this.subs.useDefaultLocationSelectors();
        return options;
    },
    getSubElement : function (subViewKey) {
        return this.$("." + subViewKey + "-container").first().get(0);
    },
    subViewAppended : function (subView) {
        return this.subs.addInstance(subView);
    }
});
TemplateBaseView.defaultConstructor = TemplateBaseView;



// WEBPACK FOOTER //
// ./~/bunsen/views/TemplateView.es.js